import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ChevronDown20Regular,
    ChevronUp20Regular,
    Dismiss20Regular,
    Mic20Filled,
    Pause20Regular,
    Play20Regular,
    RecordStop20Regular,
} from '@fluentui/react-icons';
import useMicVisualizer from '../../hooks/useMicVisualizer';
import useSpeechDictation, {
    formatSpeechDictationError,
} from '../../hooks/useSpeechDictation';
import styles from './index.module.css';

const BAR_COUNT = 48;

const formatElapsed = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const DictationPanel = ({ active, onFinish, onCancel, onError }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [committedText, setCommittedText] = useState('');
    const [interimText, setInterimText] = useState('');
    const hiddenFieldRef = useRef(null);
    const sessionActiveRef = useRef(false);

    const { audioLevel, isSpeaking, startMic, stopMic } = useMicVisualizer();

    const appendFinalText = useCallback(text => {
        const normalized = text?.trim();
        if (!normalized) {
            return;
        }

        setCommittedText(prev => (prev ? `${prev} ${normalized}` : normalized));
        setInterimText('');
    }, []);

    const handleSpeechError = useCallback(
        message => {
            onError?.(formatSpeechDictationError(message || 'Voice dictation failed.'));
        },
        [onError]
    );

    const { isPaused, pause, resume, start, stop } = useSpeechDictation({
        onFinalTranscript: appendFinalText,
        onInterimChange: setInterimText,
        onError: handleSpeechError,
    });

    const liveTranscript = useMemo(() => {
        if (!interimText) {
            return committedText;
        }
        if (!committedText) {
            return interimText;
        }
        return `${committedText} ${interimText}`;
    }, [committedText, interimText]);

    const bars = useMemo(() => Array.from({ length: BAR_COUNT }, (_, index) => index), []);

    const resetSession = useCallback(() => {
        setElapsed(0);
        setCommittedText('');
        setInterimText('');
        setCollapsed(false);
    }, []);

    const handleFinish = useCallback(async () => {
        const text = liveTranscript.trim();
        sessionActiveRef.current = false;
        await stop();
        stopMic({ playSound: true });
        resetSession();
        onFinish?.(text);
    }, [liveTranscript, onFinish, resetSession, stop, stopMic]);

    const handleCancel = useCallback(async () => {
        sessionActiveRef.current = false;
        await stop();
        stopMic({ playSound: true });
        resetSession();
        onCancel?.();
    }, [onCancel, resetSession, stop, stopMic]);

    const handleTogglePause = useCallback(async () => {
        if (isPaused) {
            try {
                await startMic();
                await resume();
            } catch (error) {
                onError?.(
                    formatSpeechDictationError(
                        error?.message || 'Could not resume voice dictation.',
                    ),
                );
            }
            return;
        }

        await pause();
        stopMic({ playSound: false });
    }, [isPaused, onError, pause, resume, startMic, stopMic]);

    useEffect(() => {
        if (!active) {
            sessionActiveRef.current = false;
            return undefined;
        }

        if (sessionActiveRef.current) {
            return undefined;
        }

        sessionActiveRef.current = true;
        resetSession();
        let cancelled = false;

        const boot = async () => {
            try {
                try {
                    await startMic();
                } catch (micError) {
                    console.warn('Mic visualizer unavailable:', micError);
                }

                if (!cancelled) {
                    await start();
                }
            } catch (error) {
                if (!cancelled) {
                    sessionActiveRef.current = false;
                    stopMic({ playSound: false });
                    await stop();
                    onError?.(
                        formatSpeechDictationError(
                            error?.message || 'Could not start voice dictation.',
                        ),
                    );
                    onCancel?.();
                }
            }
        };

        boot();

        return () => {
            cancelled = true;
        };
    }, [active, onCancel, onError, resetSession, start, startMic, stop, stopMic]);

    useEffect(() => {
        if (active) {
            return undefined;
        }

        if (!sessionActiveRef.current) {
            return undefined;
        }

        sessionActiveRef.current = false;
        stop();
        stopMic({ playSound: false });
        resetSession();

        return undefined;
    }, [active, resetSession, stop, stopMic]);

    useEffect(() => {
        if (!active || isPaused) {
            return undefined;
        }

        const interval = window.setInterval(() => {
            setElapsed(prev => prev + 1);
        }, 1000);

        return () => window.clearInterval(interval);
    }, [active, isPaused]);

    useEffect(() => {
        if (hiddenFieldRef.current) {
            hiddenFieldRef.current.value = liveTranscript;
        }
    }, [liveTranscript]);

    useEffect(() => {
        return () => {
            stop();
            stopMic({ playSound: false });
        };
    }, [stop, stopMic]);

    if (!active) {
        return null;
    }

    const statusLabel = isPaused ? 'Paused...' : 'Recording...';

    return (
        <div className={`${styles.dictationPanel} ${collapsed ? styles.dictationPanelCollapsed : ''}`}>
            <textarea
                ref={hiddenFieldRef}
                className={styles.dictationHiddenField}
                value={liveTranscript}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
            />

            <div className={styles.dictationPanelHeader}>
                <div className={styles.dictationPanelStatus}>
                    <span className={styles.dictationPanelMicIcon} aria-hidden="true">
                        <Mic20Filled color="#fff" />
                    </span>
                    <span className={styles.dictationPanelStatusText}>{statusLabel}</span>
                    <span className={styles.dictationPanelTimer}>{formatElapsed(elapsed)}</span>
                </div>

                <div className={styles.dictationPanelActions}>
                    <button type="button" className={styles.dictationPanelBtn} onClick={handleTogglePause}>
                        {isPaused ? (
                            <>
                                <Play20Regular color="var(--Brand-Secondary, #c89741)" />
                                <span>Resume</span>
                            </>
                        ) : (
                            <>
                                <Pause20Regular color="var(--Brand-Secondary, #c89741)" />
                                <span>Pause</span>
                            </>
                        )}
                    </button>
                    <button type="button" className={styles.dictationPanelBtn} onClick={handleFinish}>
                        <RecordStop20Regular color="#e5484d" />
                        <span>Finish</span>
                    </button>
                    <button type="button" className={styles.dictationPanelBtn} onClick={handleCancel}>
                        <Dismiss20Regular color="var(--Brand-Primary, #003048)" />
                        <span>Cancel</span>
                    </button>
                    <button
                        type="button"
                        className={styles.dictationPanelCollapseBtn}
                        onClick={() => setCollapsed(prev => !prev)}
                        aria-label={collapsed ? 'Expand dictation panel' : 'Collapse dictation panel'}
                        aria-expanded={!collapsed}>
                        {collapsed ? (
                            <ChevronDown20Regular color="var(--Brand-Secondary, #c89741)" />
                        ) : (
                            <ChevronUp20Regular color="var(--Brand-Secondary, #c89741)" />
                        )}
                    </button>
                </div>
            </div>

            {!collapsed && (
                <>
                    <div className={styles.dictationWaveform} aria-hidden="true">
                        {bars.map(index => {
                            const waveOffset = Math.sin((index + 1) * 0.55) * 0.22;
                            const height = isSpeaking && !isPaused ? Math.max(0.12, Math.min(1, audioLevel + waveOffset)) : 0.1 + (index % 5) * 0.015;

                            return (
                                <span
                                    key={index}
                                    className={`${styles.dictationWaveformBar} ${isSpeaking && !isPaused ? styles.dictationWaveformBarActive : styles.dictationWaveformBarIdle}`}
                                    style={{ transform: `scaleY(${height})` }}
                                />
                            );
                        })}
                    </div>

                    <div className={styles.dictationTranscriptSection}>
                        <span className={styles.dictationTranscriptLabel}>Live transcript:</span>
                        <p className={styles.dictationTranscriptText}>
                            {liveTranscript || 'Start speaking and your words will appear here...'}
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};

export default React.memo(DictationPanel);
